import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useEffect } from 'react';
import * as _ from 'lodash';
import axiosClient from 'http/axios-client';
// import SerialNumbersService from '../services/serialNumbersService';
import { useQueryParams } from './atoms/query-params-hook';
import { usePrevious } from './utils';
import CollectionKeys from './molecules/collection-keys';

const useSerialNumbers = (options = {}) => {
  const defaultOptions = {
    queryOptions: {
    },
    invalidationKeys: [],
    filterQuery: {},
  };

  const opts = _.merge(defaultOptions, options);

  const {
    page,
    setPage,
    limit,
    setLimit,
    sort,
    setSort,
  } = useQueryParams();

  const prevFilterQuery = usePrevious(opts.filterQuery);
  useEffect(() => {
    if (!_.isEqual(prevFilterQuery, opts.filterQuery)) {
      setPage(0);
    }
  }, [ opts ]);

  const makeAxiosParams = (
    {
      page: p,
      limit: l,
      sort: s,
    },
  ) => {
    const _limit = l;
    const _start = _limit * p;
    const _sort = `${ s.orderBy }:${ s.order.toUpperCase() }`;

    return {
      _start,
      _limit,
      _sort,
    };
  };

  const endpoint = `${ CollectionKeys.SERIAL_NUMBER }/`;

  const queryClient = useQueryClient();

  const {
    data: serials,
    isLoading: serialsLoading,
    error: serialsError,
    refetch,
  } = useQuery(
    [ endpoint, { page, limit, sort, ...opts.filterQuery }],
    () => {
      return axiosClient.get(endpoint, {
        params: {
          ...makeAxiosParams({ page, limit, sort }),
          ...opts.filterQuery,
        },
      });
    },
    {
      ...opts.queryOptions,
      keepPreviousData: true,
    },
  );

  const {
    data: count,
    isLoading: countIsLoading,
  } = useQuery(
    [ endpoint, 'COUNT' ],
    () => {
      return axiosClient.get(`${ endpoint }/count`, {
        params: {
          _limit: -1,
        },
      });
    },
    {
      ...opts.queryOptions,
    },
  );

  const {
    data: countFiltered,
    isLoading: countFilteredIsLoading,
  } = useQuery(
    [ endpoint, 'COUNT', { ...opts.filterQuery }],
    () => {
      return axiosClient.get(`${ endpoint }/count`, {
        params: {
          _limit: -1,
          ...opts.filterQuery,
        },
      });
    },
    {
      ...opts.queryOptions,
    },
  );

  // Prefetch the next page!
  useEffect(() => {
    if ([ countFiltered, page, limit ].some((metric) => { return 'number' !== typeof metric; })) {
      return;
    }
    const nextPage = page + 1;
    const hasMore = countFiltered > nextPage * limit;
    if (hasMore) {
      queryClient.prefetchQuery(
        [ endpoint, { page: nextPage, limit, sort, ...opts.filterQuery }],
        () => {
          return axiosClient.get(endpoint, {
            params: {
              ...makeAxiosParams({ page: nextPage, limit, sort }),
              ...opts.filterQuery,
            },
          });
        },
      );
    }
  }, [ countFiltered, page, limit, queryClient ]);

  // fix page
  useEffect(() => {
    if ([ countFiltered, page, limit ].some((metric) => { return 'number' !== typeof metric; })) {
      return;
    }
    const lastPossiblePage = Math.floor(Math.max(0, countFiltered - 1) / limit);

    if (lastPossiblePage < page) {
      setPage(lastPossiblePage);
    }
  }, [ countFiltered, page, limit ]);

  return {
    serials,
    serialsLoading,
    serialsError,
    count,
    countIsLoading,
    countFiltered,
    countFilteredIsLoading,
    refetch,
    pagination: {
      page,
      setPage,
      limit,
      setLimit,
      sort,
      setSort,
      count: countFiltered,
    },
  };
};

export default useSerialNumbers;
/// //////////

export const useSerialsPerThematic = (collectionName, itemId, options = {}) => {
  const defaultOptions = {
    queryOptions: {
    },
    invalidationKeys: [],
    filterQuery: {},
  };

  const opts = _.merge(defaultOptions, options);
  const {
    page,
    setPage,
    limit,
    setLimit,
    sort,
    setSort,
  } = useQueryParams();

  const prevFilterQuery = usePrevious(opts.filterQuery);
  useEffect(() => {
    if (!_.isEqual(prevFilterQuery, opts.filterQuery)) {
      setPage(0);
    }
  }, [ opts ]);

  const makeAxiosParams = (
    {
      page: p,
      limit: l,
      sort: s,
    },
  ) => {
    const _limit = l;
    const _start = _limit * p;
    const _sort = `${ s.orderBy }:${ s.order.toUpperCase() }`;

    return {
      _start,
      _limit,
      _sort,
    };
  };

  const endpoint = `${ collectionName }/${ itemId }`;

  const queryClient = useQueryClient();

  const invalidateKeys = () => {
    return queryClient.invalidateQueries([ endpoint, { page, limit, sort, ...opts.filterQuery }]);
  };

  const {
    data: serials,
    isLoading: serialsLoading,
    error: serialsError,
    refetch,
  } = useQuery(
    [ endpoint, { page, limit, sort, ...opts.filterQuery }],
    () => {
      return axiosClient.get(endpoint, {
        params: {
          ...makeAxiosParams({ page, limit, sort }),
          ...opts.filterQuery,
        },
      });
    },
    {
      ...opts.queryOptions,
      keepPreviousData: true,
    },
  );

  const {
    data: count,
    isLoading: countIsLoading,
  } = useQuery(
    [ endpoint, 'COUNT' ],
    () => {
      return axiosClient.get(`${ endpoint }/count`, {
        params: {
          _limit: -1,
        },
      });
    },
    {
      ...opts.queryOptions,
    },
  );

  const {
    data: countFiltered,
    isLoading: countFilteredIsLoading,
  } = useQuery(
    [ endpoint, 'COUNT', { ...opts.filterQuery }],
    () => {
      return axiosClient.get(`${ endpoint }/count`, {
        params: {
          _limit: -1,
          ...opts.filterQuery,
        },
      });
    },
    {
      ...opts.queryOptions,
    },
  );

  // Prefetch the next page!
  useEffect(() => {
    if ([ countFiltered, page, limit ].some((metric) => { return 'number' !== typeof metric; })) {
      return;
    }
    const nextPage = page + 1;
    const hasMore = countFiltered > nextPage * limit;
    if (hasMore) {
      queryClient.prefetchQuery(
        [ `${ collectionName }`, { page: nextPage, limit, sort, ...opts.filterQuery }],
        () => {
          return axiosClient.get(endpoint, {
            params: {
              ...makeAxiosParams({ page: nextPage, limit, sort }),
              ...opts.filterQuery,
            },
          });
        },
      );
    }
  }, [ countFiltered, page, limit, queryClient ]);

  const {
    mutateAsync: updateSerial,
    isLoading: serialIsUpdating,
  } = useMutation(
    [ `${ collectionName }`, 'UPDATE' ],
    ({ id, payload }) => {
      queryClient.setQueryData([ endpoint, { page, limit, sort, ...opts.filterQuery }],
        (oldData) => {
          const foundSerial = oldData?.find((serial) => { return serial.id === id; });
          if (foundSerial) {
            Object.entries(payload).forEach(([ key, value ]) => {
              foundSerial[key] = value;
            });
          }
          return oldData;
        });

      return axiosClient.put(`${ CollectionKeys.SERIAL_NUMBER }/${ id }`, payload);
    },
    {
      onSuccess() {
      },
      onSettled() {
        return invalidateKeys();
      },
    },
  );

  // fix page
  useEffect(() => {
    if ([ countFiltered, page, limit ].some((metric) => { return 'number' !== typeof metric; })) {
      return;
    }
    const lastPossiblePage = Math.floor(Math.max(0, countFiltered - 1) / limit);

    if (lastPossiblePage < page) {
      setPage(lastPossiblePage);
    }
  }, [ countFiltered, page, limit ]);

  return {
    serials,
    serialsLoading,
    serialsError,
    count,
    updateSerial,
    serialIsUpdating,
    countIsLoading,
    countFiltered,
    countFilteredIsLoading,
    refetch,
    pagination: {
      page,
      setPage,
      limit,
      setLimit,
      sort,
      setSort,
      count: countFiltered,
    },
  };
};
